/**
 * Everything sent about one work order: to whom, by which channel, at which
 * number or address, and how it went — dispatch messages and rule messages
 * alike. A message that could not go says why (no provider set up, nothing on
 * file), which is the difference between "nobody was told" and "somebody was
 * told and ignored it".
 */
import { Link } from "react-router-dom";
import { useWorkOrderMessages } from "../../api/queries";
import { Card, EmptyState, ErrorState, Skeleton } from "../../design/components";
import { CHANNEL_LABEL, EVENT_LABEL, type NoticeEvent } from "../../domain/contacts";
import { t } from "../../i18n";
import s from "./Contacts.module.css";

const STATUS_LABEL: Record<string, string> = {
  queued: "queued",
  sent: "sent",
  failed: "failed",
  no_address: "nothing on file",
  not_configured: "waiting for a provider",
  waiting: "waiting for the app",
  delivered: "delivered",
  read: "read",
};

export function WorkOrderMessages({ workOrderId }: { workOrderId: string }) {
  const q = useWorkOrderMessages(workOrderId);
  const rows = q.data ?? [];
  const people = new Set(rows.map((r) => r.recipient)).size;
  return (
    <Card
      title="Messages"
      sub={
        rows.length
          ? t("{n} messages to {p} people by SMS, email and the mobile app", { n: rows.length, p: people })
          : t("who was told about this work order, and how it went")
      }
      testId="wo-messages"
      collapsible
    >
      {q.isPending ? (
        <Skeleton rows={3} />
      ) : q.isError ? (
        <ErrorState error={q.error} retry={() => void q.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState>
          {t("Nobody has been messaged about this work order.")} <Link to="/notify-rules">{t("Who is told →")}</Link>
        </EmptyState>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className={s.msgTable}>
            <thead>
              <tr>
                <th>{t("When")}</th>
                <th>{t("About")}</th>
                <th>{t("To")}</th>
                <th>{t("How")}</th>
                <th>{t("Where")}</th>
                <th>{t("Status")}</th>
                <th>{t("Why")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id} data-channel={m.channel}>
                  <td>{m.createdAt ? m.createdAt.slice(11, 16) : ""}</td>
                  <td>{m.event ? t(EVENT_LABEL[m.event as NoticeEvent] ?? m.event) : ""}</td>
                  <td>{m.recipient}</td>
                  <td>{t(CHANNEL_LABEL[m.channel])}</td>
                  <td style={{ fontFamily: "var(--mono)", fontSize: 12.5 }}>
                    {m.channel === "app" ? "—" : (m.to ?? "—")}
                  </td>
                  <td>
                    <span className={s.status} data-status={m.status}>
                      {t(STATUS_LABEL[m.status] ?? m.status)}
                    </span>
                  </td>
                  <td className={s.dim}>
                    {m.rule ? t("rule: {r}", { r: m.rule }) : m.group ? t("dispatch") : t("dispatch")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
